import { LeafletExtensionGlobalFilterBasedLayer } from './LeafletExtensionGlobalFilterBasedLayer';
import { NiLayerOptions } from './NIBaseClass/LeafletExtensionNIBaseLayer';
// import { SqmFeatureItem, MarkerData, Segment } from 'types/types';

interface SqmFeatureItem {
  site_natural_key: string;
  latitude: number;
  longitude: number;
  sqm_service: string;
  device_brand: string;
  failure_cause: string;
  Total_Unique_Subscribers_on_Site_Service: number;
  Failure_Count: number;
};

interface MarkerData {
  location: L.LatLngExpression;
  values: {
    [dataType: string]: {
      total_subscribers: number;
      items: Record<string, Segment[]>;
      label: string;
      total_failure_count: number;
    };
  };
  site_natural_key: string;
  servicesProcessed: Set<string>;
}

interface Segment {
  value: number;
  color: string;
  label: string;
  percentage: number;
  sqm_service: string;
}


const pieMarkersSizesPerZoom = {
  20: 150,
  19: 150,
  18: 150,
  17: 120,
  16: 90,
  15: 60,
  14: 30,
  13: 30,
  12: 30,
  11: 30,
  10: 30,
};

export default class SQMRenderingLayer extends LeafletExtensionGlobalFilterBasedLayer {
  private combinedData: any[] = [];
  private maxSubscribers: number = 0;
  private currentSize: number;

  constructor(options: NiLayerOptions) {
    super(options);
    this.currentSize = 30;
  }
  protected renderLayerData() {
    const colors = this.getLegendColors();

    const combinedDataObject = this.aggregateLayerData();

    this.combinedData = this.finalizeCombinedData(combinedDataObject, colors);

    this.renderPieCharts();
  }

  onAdd(map: L.Map): this {
    super.onAdd(map);
    this._map = map;
    this.renderPieCharts();
    this._map.on('zoomend', this.renderPieCharts, this);
    this._map.on('moveend', this.renderPieCharts, this);
    return this;
  }

  onRemove(map: L.Map): this {
    super.onRemove(map);
    this._map.off('zoomend', this.renderPieCharts, this);
    this._map.off('moveend', this.renderPieCharts, this);
    return this;
  }

  private getLegendColors(): string[] {
    return this.niLayerOptions?.layerDefinition?.LayerProperties
      ?.LegendValueToColorMapping
      ? Object.values(
          this.niLayerOptions.layerDefinition.LayerProperties
            .LegendValueToColorMapping
        )
      : [];
  }

  private getPieType(): string {
    return this.niLayerOptions.layerDefinition.LayerId === 'SQM_BRANDS'
      ? 'device_brands'
      : 'failure_causes';
  }

  private aggregateLayerData(): Record<string, any> {
    return this.layerDataFromServer?.data?.features.reduce(
      (finalAcc: Record<string, any>, feature) => {
        Object.values(
          feature.properties.data.reduce(
            (acc: Record<string, any>, item: SqmFeatureItem) => {
              const key = `${item.site_natural_key}_${item.latitude}_${item.longitude}`;

              if (!acc[key]) {
                acc[key] = this.initializeAccumulator(item);
              }

              this.aggregateItemData(acc[key], item);
              return acc;
            },
            {}
          )
        ).forEach((result: any) => {
          this.maxSubscribers = Math.max(
            this.maxSubscribers,
            result.values.device_brands.total_subscribers,
            result.values.failure_causes.total_subscribers
          );
          this.mergeIntoFinalAccumulator(finalAcc, result);
        });

        return finalAcc;
      },
      {}
    );
  }

  private initializeAccumulator(item: SqmFeatureItem): MarkerData {
    return {
      site_natural_key: item.site_natural_key,
      location: {
        lat: item.latitude,
        lng: item.longitude,
      },
      values: {
        device_brands: {
          items: {},
          total_subscribers: 0,
          label: 'Device Brands',
          total_failure_count: 0,
        },
        failure_causes: {
          items: {},
          total_subscribers: 0,
          label: 'Failure causes',
          total_failure_count: 0,
        },
      },
      servicesProcessed: new Set<string>(),
    };
  }

  private aggregateItemData(acc: any, item: SqmFeatureItem): void {
    const device_key = `${item.sqm_service}: ${item.device_brand}`;
    const cause_key = `${item.sqm_service}: ${item.failure_cause}`;
    const serviceKey = `${item.site_natural_key}_${item.sqm_service}`;

    if (!acc.servicesProcessed.has(serviceKey)) {
      acc.values.device_brands.total_subscribers +=
        item.Total_Unique_Subscribers_on_Site_Service;
      acc.values.failure_causes.total_subscribers +=
        item.Total_Unique_Subscribers_on_Site_Service;
      acc.servicesProcessed.add(serviceKey);
    }

    if (!acc.values.device_brands.items[device_key]) {
      acc.values.device_brands.items[device_key] = {
        name: device_key,
        value: 0,
        sqm_service: item.sqm_service,
        label: item.device_brand,
      };
    }
    acc.values.device_brands.items[device_key].value += item.Failure_Count;
    acc.values.device_brands.total_failure_count += item.Failure_Count;

    if (!acc.values.failure_causes.items[cause_key]) {
      acc.values.failure_causes.items[cause_key] = {
        name: cause_key,
        value: 0,
        sqm_service: item.sqm_service,
        label: item.failure_cause,
      };
    }
    acc.values.failure_causes.items[cause_key].value += item.Failure_Count;
    acc.values.failure_causes.total_failure_count += item.Failure_Count;
  }

  private mergeIntoFinalAccumulator(
    finalAcc: Record<string, any>,
    result: any
  ): void {
    const finalKey = `${result.site_natural_key}_${result.location.lat}_${result.location.lng}`;
    if (!finalAcc[finalKey]) {
      finalAcc[finalKey] = result;
    } else {
      finalAcc[finalKey].values.device_brands.total_subscribers +=
        result.values.device_brands.total_subscribers;
      finalAcc[finalKey].values.failure_causes.total_subscribers +=
        result.values.failure_causes.total_subscribers;
      type DeviceBrandItem = {
        value: number;
        sqm_service: string;
        label: string;
      };
      const items = result.values.device_brands.items as Record<
        string,
        DeviceBrandItem
      >;
      for (const [key, value] of Object.entries(items)) {
        if (!finalAcc[finalKey].values.device_brands.items[key]) {
          finalAcc[finalKey].values.device_brands.items[key] = value;
        } else {
          finalAcc[finalKey].values.device_brands.items[key].value +=
            value.value;
        }
      }
      const failureItems = result.values.device_brands.items as Record<
        string,
        DeviceBrandItem
      >;
      for (const [key, value] of Object.entries(failureItems)) {
        if (!finalAcc[finalKey].values.failure_causes.items[key]) {
          finalAcc[finalKey].values.failure_causes.items[key] = value;
        } else {
          finalAcc[finalKey].values.failure_causes.items[key].value +=
            value.value;
        }
      }
    }
  }

  private finalizeCombinedData(
    combinedDataObject: Record<string, any>,
    colors: string[]
  ): any[] {
    return Object.values(combinedDataObject).map((entry: any) => {
      entry.values.device_brands.items = this.groupItemsByService(
        entry.values.device_brands.items,
        entry.values.device_brands.total_failure_count,
        colors
      );

      entry.values.failure_causes.items = this.groupItemsByService(
        entry.values.failure_causes.items,
        entry.values.failure_causes.total_failure_count,
        colors
      );

      return entry;
    });
  }

  private groupItemsByService(
    items: Record<string, any>,
    total_failure_count: number,
    colors: string[]
  ): Record<string, any[]> {
    const grouped: Record<string, any[]> = {};
    Object.values(items).forEach((item: any) => {
      const serviceName = item.sqm_service;
      const percentage = (item.value / total_failure_count) * 100;
      const colorIndex = Math.min(
        Math.floor(percentage / 5),
        colors.length - 1
      );
      const formattedItem = {
        ...item,
        percentage: percentage.toFixed(2),
        color: colors[colorIndex],
      };
      if (!grouped[serviceName]) {
        grouped[serviceName] = [];
      }
      grouped[serviceName].push(formattedItem);
    });
    return grouped;
  }

  public generatePieChart(map: L.Map, marker: MarkerData): L.Polygon[] {
    const zoomLevel = map.getZoom();
    const maxSize = this._getPieChartSizeForZoom(zoomLevel) || 250;
    const totalSubscribers = marker.values[this.getPieType()]?.total_subscribers || 1;

    this.currentSize = (totalSubscribers / this.maxSubscribers) * maxSize < 10
      ? 10
      : (totalSubscribers / this.maxSubscribers) * maxSize;

    const centerLatLng = marker.location;

    // Remove existing pie chart segments
    let piePolygons: L.Polygon[] = [];

    const segments = Object.values(marker.values[this.getPieType()]?.items).flat();
    const totalValue = segments.reduce((sum, segment) => sum + segment.value, 0) || 1;
    let startAngle = 0;

    const popupContent = this._generatePopupContent(segments);

    segments.forEach((segment) => {
      const endAngle = startAngle + (segment.value / totalValue) * 360;
      const points = this._calculatePieSlicePoints(centerLatLng, this.currentSize, startAngle, endAngle, map);
      const color = segment.color || '#000';

      const pieSlice = L.polygon(points, {
        fillColor: color,
        fillOpacity: 1,
        weight: 0,
      }).bindPopup(popupContent, { className: 'pie-chart-popup' });

      piePolygons.push(pieSlice);
      startAngle = endAngle;
    });

    return piePolygons;
  }

  private _generatePopupContent(segments: Segment[]): string {
    // Group segments by `sqm_service`
    const groupedSegments = segments.reduce((acc: Record<string, Segment[]>, segment) => {
        const groupKey = segment.sqm_service;
        if (!acc[groupKey]) {
            acc[groupKey] = [];
        }
        acc[groupKey].push(segment);
        return acc;
    }, {});

    // Generate HTML content
    return Object.entries(groupedSegments)
      .map(([sqmService, serviceSegments]) => {
          const totalValue = serviceSegments.reduce((sum, seg) => sum + seg.value, 0);
          const groupTitle = `<h3>${sqmService} (Total: ${totalValue})</h3>`;
          const groupContent = serviceSegments
              .map(
                  (segment) =>
                      `<div class="legend-entry">
                          <span class="square" style="background-color: ${segment.color}"></span>
                          <div><strong>${segment.label}</strong>: ${segment.value} (${segment.percentage}%)</div>
                      </div>`
              )
              .join('');
          return `${groupTitle}${groupContent}`;
      })
      .join('');
  }

  private _calculatePieSlicePoints(
    centerLatLng: L.LatLngExpression,
    radius: number,
    startAngle: number,
    endAngle: number,
    map: L.Map
  ): L.LatLng[] {
    const points: L.LatLng[] = [L.latLng(centerLatLng)];
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const numPoints = 30;
    for (let i = 0; i <= numPoints; i++) {
      const angle = startRad + (i / numPoints) * (endRad - startRad);
      points.push(this._pointOnCircle(centerLatLng, radius, angle, map));
    }

    return points;
  }

  private _pointOnCircle(centerLatLng: L.LatLngExpression, radius: number, angle: number, map: L.Map): L.LatLng {
    const centerPoint = map.latLngToLayerPoint(centerLatLng);
    const x = centerPoint.x + radius * Math.cos(angle);
    const y = centerPoint.y + radius * Math.sin(angle);
    return map.layerPointToLatLng([x, y]);
  }

  private _getPieChartSizeForZoom(zoomLevel: number): number {
    return pieMarkersSizesPerZoom[zoomLevel];
  }

  private renderPieCharts(): void {
    const bounds = this._map.getBounds();
    this.internalLayer.clearLayers();
    this.combinedData.forEach((marker: any) => {
      if (bounds.contains(marker.location)) {
        const pieChartSegments = marker['pieChartSegments'] || this.generatePieChart(
          this._map!,
          marker
        );
        pieChartSegments.forEach((segment) =>
          this.internalLayer.addLayer(segment)
        );
        marker['pieChartSegments'] = pieChartSegments;
      }else{
        marker['pieChartSegments']?.forEach((segment) =>
          this.internalLayer.removeLayer(segment)
        );
      }
    });
  }
}
